import { z } from 'zod'
import type { LowerHex } from '../../types'
import {
  addressPrimitiveSchema,
  lowerHexSchema,
  txHashSchema,
} from '../../types/primitive'

const numericTextSchema = z.union([z.string(), z.number(), z.bigint()])
const decimalTextSchema = numericTextSchema
const optionalAddressSchema = z
  .union([addressPrimitiveSchema, z.literal(''), z.null(), z.undefined()])
  .transform((value) =>
    typeof value === 'string' && value.length > 0 ? value : undefined,
  )

/**
 * 10 進数表記の数量を指定 decimals の最小単位 bigint に変換する。
 *
 * @param parameters - 変換対象の値と小数桁数。
 * @returns 最小単位に変換した bigint。
 */
export const parseDecimalToBigInt = ({
  value,
  decimals,
}: {
  value: string | number | bigint
  decimals: number
}): bigint => {
  const text = value.toString().trim()
  if (!/^\d+(\.\d+)?$/.test(text)) {
    throw new TypeError(`Invalid decimal value: ${text}`)
  }
  const [whole, fractional = ''] = text.split('.')
  if (fractional.length > decimals) {
    throw new TypeError(`Too many decimal places: ${text}`)
  }
  const scale = 10n ** BigInt(decimals)
  const scaledFractional = fractional.padEnd(decimals, '0')
  return (
    BigInt(whole) * scale +
    BigInt(scaledFractional.length === 0 ? '0' : scaledFractional)
  )
}

/**
 * KAIA 表記の数量を peb に変換する zod schema。
 */
export const kaiaAmountSchema = decimalTextSchema.transform((value) =>
  parseDecimalToBigInt({ value, decimals: 18 }),
)

/**
 * tokenDecimal を考慮して token transfer の数量を bigint に変換する。
 *
 * @param parameters - API から返る value と tokenDecimal。
 * @returns token 最小単位の bigint。
 */
export const parseTokenAmount = ({
  value,
  tokenDecimal,
}: {
  value: string | number | bigint
  tokenDecimal?: string | number | bigint | null
}): bigint => {
  const text = value.toString().trim()
  if (/^\d+$/.test(text)) {
    return BigInt(text)
  }
  if (
    tokenDecimal === undefined ||
    tokenDecimal === null ||
    tokenDecimal.toString().trim().length === 0
  ) {
    throw new TypeError(`tokenDecimal is required for decimal value: ${text}`)
  }
  const decimals = Number(tokenDecimal)
  if (!Number.isInteger(decimals) || decimals < 0) {
    throw new TypeError(`Invalid tokenDecimal: ${tokenDecimal}`)
  }
  return parseDecimalToBigInt({ value, decimals })
}

/**
 * mixed-case hex を lower-case hex として parse する。
 */
export const mixedCaseLowerHexSchema = z
  .string()
  .transform((value) => value.toLowerCase())
  .pipe(lowerHexSchema)

/**
 * input 欄の null 相当値を `0x` に正規化して parse する。
 */
export const inputHexSchema = z
  .union([z.string(), z.null(), z.undefined()])
  .transform((value) => {
    if (value === undefined || value === null) {
      return '0x'
    }
    const normalized = value.trim()
    return normalized === '' || normalized.toLowerCase() === 'null'
      ? '0x'
      : normalized.toLowerCase()
  })
  .pipe(lowerHexSchema)

/**
 * Kaiascan の timestamp 欄を秒からミリ秒へ変換する schema。
 */
export const timestampSecondsSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .transform((value) => value.toString().trim())
  .refine((value) => /^\d+$/.test(value))
  .transform((value) => Number(value) * 1000)

/**
 * Kaiascan 固有の timestamp 欄をミリ秒へ正規化する schema。
 */
export const timestampMillisecondsSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .transform((value) => value.toString().trim())
  .refine((value) => /^\d+$/.test(value))
  .transform((value) => {
    const timestamp = Number(value)
    return value.length <= 10 ? timestamp * 1000 : timestamp
  })

/**
 * Kaiascan の数値文字列を number に変換する schema。
 */
export const numberTextSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .transform((value) => value.toString().trim())
  .refine((value) => /^\d+$/.test(value))
  .transform((value) => Number(value))

/**
 * Kaiascan の整数文字列を bigint に変換する schema。
 */
export const bigintIntegerSchema = z
  .union([z.string(), z.number(), z.bigint()])
  .transform((value) => value.toString().trim())
  .refine((value) => /^\d+$/.test(value))
  .transform((value) => BigInt(value))

/**
 * Kaiascan OAPI の boolean 相当値を失敗フラグに正規化する schema。
 */
export const kaiascanOapiFailureFlagSchema = z
  .unknown()
  .optional()
  .transform((value) => {
    if (value === undefined || value === null) {
      return false
    }
    if (typeof value === 'boolean') {
      return value
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      return normalized === 'true' || normalized === '1'
    }
    if (typeof value === 'number') {
      return value === 1
    }
    if (typeof value === 'bigint') {
      return value === 1n
    }
    return true
  })

/**
 * 空文字や null を optional address に変換する schema。
 */
export const optionalKaiascanAddressSchema = optionalAddressSchema

/**
 * Kaiascan Etherscan-compatible normal tx item schema。
 */
export const kaiascanTransactionSchema = z
  .object({
    blockNumber: numberTextSchema,
    hash: mixedCaseLowerHexSchema,
    input: inputHexSchema,
    timeStamp: timestampSecondsSchema.optional(),
    timestamp: timestampMillisecondsSchema.optional(),
    value: kaiaAmountSchema,
  })
  .transform((value) => ({
    blockNumber: value.blockNumber,
    hash: value.hash,
    input: value.input,
    timestamp: value.timeStamp ?? value.timestamp,
    value: value.value,
  }))
  .pipe(
    z.object({
      blockNumber: z.number(),
      hash: txHashSchema,
      input: lowerHexSchema,
      timestamp: z.number(),
      value: z.bigint(),
    }),
  )

/**
 * Kaiascan Etherscan-compatible internal tx item schema。
 */
export const kaiascanInternalTransactionSchema = z
  .object({
    traceId: z.string().regex(/^\d+$/).optional(),
    blockNumber: numberTextSchema,
    contractAddress: optionalKaiascanAddressSchema,
    from: addressPrimitiveSchema,
    gas: bigintIntegerSchema,
    hash: mixedCaseLowerHexSchema,
    isError: z
      .union([z.string(), z.number(), z.boolean()])
      .transform((value) =>
        typeof value === 'boolean' ? value : value.toString() === '1',
      ),
    timeStamp: timestampSecondsSchema.optional(),
    timestamp: timestampMillisecondsSchema.optional(),
    to: optionalKaiascanAddressSchema,
    value: kaiaAmountSchema,
  })
  .transform((value) => ({
    ...value,
    timestamp: value.timeStamp ?? value.timestamp,
  }))
  .pipe(
    z.object({
      traceId: z.string().optional(),
      blockNumber: z.number(),
      contractAddress: addressPrimitiveSchema.optional(),
      from: addressPrimitiveSchema,
      gas: z.bigint(),
      hash: txHashSchema,
      isError: z.boolean(),
      timestamp: z.number(),
      to: addressPrimitiveSchema.optional(),
      value: z.bigint(),
    }),
  )

/**
 * Kaiascan Etherscan-compatible token transfer item schema。
 */
export const kaiascanTokenTransferSchema = z
  .object({
    blockNumber: numberTextSchema,
    contractAddress: z.union([
      addressPrimitiveSchema,
      z.literal(''),
      z.null(),
      z.undefined(),
    ]),
    from: addressPrimitiveSchema,
    hash: mixedCaseLowerHexSchema,
    logIndex: numberTextSchema.optional(),
    timeStamp: timestampSecondsSchema.optional(),
    timestamp: timestampMillisecondsSchema.optional(),
    to: addressPrimitiveSchema,
    tokenDecimal: z
      .union([z.string(), z.number(), z.bigint(), z.null()])
      .optional(),
    tokenValue: decimalTextSchema.optional(),
    value: decimalTextSchema.optional(),
  })
  .transform((value) => {
    const amount = value.value ?? value.tokenValue
    if (amount === undefined) {
      throw new TypeError('value or tokenValue is required')
    }
    return {
      blockNumber: value.blockNumber,
      contractAddress: value.contractAddress,
      from: value.from,
      hash: value.hash,
      logIndex: value.logIndex ?? 0,
      timestamp: value.timeStamp ?? value.timestamp,
      to: value.to,
      value: parseTokenAmount({
        value: amount,
        tokenDecimal: value.tokenDecimal,
      }),
    }
  })
  .pipe(
    z.object({
      blockNumber: z.number(),
      contractAddress: z.union([
        addressPrimitiveSchema,
        z.literal(''),
        z.null(),
        z.undefined(),
      ]),
      from: addressPrimitiveSchema,
      hash: txHashSchema,
      logIndex: z.number(),
      timestamp: z.number(),
      to: addressPrimitiveSchema,
      value: z.bigint(),
    }),
  )

/**
 * Kaiascan OAPI event log item schema。
 */
export const kaiascanOapiLogSchema = z.object({
  block_number: numberTextSchema,
  contract_address: addressPrimitiveSchema,
  data: mixedCaseLowerHexSchema,
  log_index: numberTextSchema,
  topics: z.array(mixedCaseLowerHexSchema.nullable()).transform((topics) => {
    const values: LowerHex[] = []
    for (const topic of topics) {
      if (topic !== null) {
        values.push(topic)
      }
    }
    return values
  }),
  transaction_hash: mixedCaseLowerHexSchema.pipe(txHashSchema),
})

/**
 * Kaiascan transaction info schema。
 */
export const kaiascanTransactionInfoSchema = z
  .object({
    blockNumber: numberTextSchema.optional(),
    block_number: numberTextSchema.optional(),
    timeStamp: timestampSecondsSchema.optional(),
    timestamp: timestampMillisecondsSchema.optional(),
  })
  .transform((value) => ({
    blockNumber: value.blockNumber ?? value.block_number,
    timestamp: value.timeStamp ?? value.timestamp,
  }))
  .pipe(
    z.object({
      blockNumber: z.number(),
      timestamp: z.number(),
    }),
  )

/**
 * Kaiascan OAPI internal tx item schema。
 */
export const kaiascanOapiInternalTransactionSchema = z.object({
  level: numberTextSchema.optional(),
  amount: kaiaAmountSchema.optional(),
  error: kaiascanOapiFailureFlagSchema,
  from: z.object({
    address: addressPrimitiveSchema,
  }),
  gas_limit: bigintIntegerSchema.optional(),
  reverted: kaiascanOapiFailureFlagSchema,
  to: z
    .object({
      address: addressPrimitiveSchema.optional(),
    })
    .optional(),
})
